<script setup lang="ts">
import type { UserV2IconName } from './types';
import { localeLink } from '@/utils/template';

// Item "Menu Opção" do Figma — usado na tela Dados (mobile)
// e na sidebar do painel desktop.
interface Props {
  label: string;
  icon: UserV2IconName;
  to?: string;
  expandable?: boolean;
  expanded?: boolean;
  active?: boolean;
  danger?: boolean;
  // "Em breve": trava o item (blur + cadeado) e desativa navegação/click.
  comingSoon?: boolean;
  // Atalho de um item expandido (Figma node 19771:52178, "Jogo Responsável
  // Open"): sem o fundo bg-v2-default/40 do item pai (só o pai fica
  // destacado, os atalhos ficam "soltos" no fundo do painel — daí a
  // aparência mais escura) e sem a seta chevron-right no fim da linha.
  nested?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  to: '',
  expandable: false,
  expanded: false,
  active: false,
  danger: false,
  comingSoon: false,
  nested: false,
});

defineEmits<{ click: []; toggle: [] }>();

// resolveComponent só funciona no setup — no template ele resolve pra
// string e o item vira elemento morto (não navega)
const NuxtLinkComponent = resolveComponent('NuxtLink');
</script>

<template>
  <component
    :is="props.comingSoon ? 'div' : props.to ? NuxtLinkComponent : 'button'"
    class="relative flex w-full shrink-0 items-center gap-[8px] overflow-hidden rounded-[12px] px-[11px] py-[10px] text-left text-[12px] leading-[16px]"
    :class="[
      props.active
        ? 'border border-solid border-v2-primary bg-v2-primary/20 text-white'
        : props.nested
          ? 'text-v2-default-900'
          : 'bg-v2-default/40 text-white',
      props.danger ? 'text-v2-danger' : '',
      props.comingSoon ? 'cursor-default' : '',
    ]"
    :to="!props.comingSoon && props.to ? localeLink(props.to) : undefined"
    @click="!props.comingSoon && $emit('click')"
  >
    <UserV2Icon :name="props.icon" />
    <span class="flex-1 font-medium">{{ props.label }}</span>
    <!-- Chevron da expansão: alvo de clique próprio (span pra não aninhar
         button/link). Expande sem navegar; a linha em si continua indo pro
         `to` (ex.: Jogo Responsável → tela geral). -->
    <span
      v-if="props.expandable && !props.comingSoon"
      aria-label="Expandir"
      class="flex shrink-0 cursor-pointer items-center justify-center opacity-70"
      :class="{ 'rotate-180': props.expanded }"
      role="button"
      tabindex="0"
      @click.stop.prevent="$emit('toggle')"
      @keydown.enter.stop.prevent="$emit('toggle')"
    >
      <UserV2Icon name="chevron-down" />
    </span>
    <UserV2Icon
      v-else-if="props.to && !props.comingSoon && !props.nested"
      class="opacity-70"
      name="chevron-right"
    />

    <BaseComingSoonOverlay
      v-if="props.comingSoon"
      rounded="rounded-[12px]"
    />
  </component>
</template>
